import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import MobileNav from './MobileNav';
import Spinner from '../common/Spinner';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import useMyProfileStore, { selectNoSchoolAssigned } from '../../store/useMyProfileStore';
import useAuthStore from '../../store/useAuthStore';
import { navForContext, PATHS } from '../../routes/navConfig';

/* Nothing to manage: either the account has no school/class linked to it yet,
   or the profile never came back (`failed`) — there is no demo data to fall
   back on, so say which it is and offer a retry. */
function NoSchoolAssigned({ failed }) {
  const { loading, fetchMyProfile } = useMyProfileStore();
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const signOut = () => {
    useMyProfileStore.getState().reset();
    useContextStore.getState().resetContexts();
    logout();
    navigate(PATHS.LOGIN);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)', padding: 24 }}>
      <div className="card" style={{ maxWidth: 440, textAlign: 'center', padding: '34px 30px' }}>
        <img src="/assets/icons/school.svg" alt="" style={{ width: 44, height: 44, marginBottom: 12 }} />
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 600, color: 'var(--ink)', margin: '0 0 8px' }}>
          {failed ? "We couldn't load your classes" : 'No class assigned yet'}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 20px' }}>
          {failed
            ? "Your profile didn't come back from the server, so there's nothing to show yet. Check your connection and try again."
            : "Your account isn't linked to a school or class, so there's nothing to manage here yet. Set up your class in the app or ask your school admin for access, then try again."}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button className="btn btn-primary" disabled={loading} onClick={fetchMyProfile}>
            {loading ? <><Spinner /> Checking…</> : 'Check Again'}
          </button>
          <button className="btn btn-ghost" onClick={signOut}>Sign Out</button>
        </div>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const ctx = useContextStore(selectCurrentContext);
  const location = useLocation();
  const { me, loaded, fetchMyProfile } = useMyProfileStore();
  const noSchoolAssigned = useMyProfileStore(selectNoSchoolAssigned);

  /* The signed-in user's profile loads before any page renders — after login and
     on every reload while a token exists. It hydrates identity + contexts. */
  useEffect(() => {
    fetchMyProfile();
  }, [fetchMyProfile]);

  if (!loaded) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <Spinner variant="green" />
      </div>
    );
  }

  /* No context = no school/class on the profile, or no profile at all. Nothing
     below here can render without one, so stop at the empty state. */
  if (noSchoolAssigned || !ctx) {
    return <NoSchoolAssigned failed={!me} />;
  }

  /* Master and class contexts expose different sections; Profile is reachable
     from both. Three class modules hang off the Links hub with no nav item of
     their own — Sign-Ups, Reminders and Donation Links — so each has to be
     listed here or it lands back on the dashboard. */
  const allowed = [
    ...navForContext(ctx).map((n) => n.path),
    PATHS.PROFILE,
    ...(ctx.type === 'master' ? [] : [PATHS.SIGNUPS, PATHS.REMINDERS, PATHS.DONATION_LINKS]),
  ];
  if (!allowed.includes(location.pathname)) {
    return <Navigate to={PATHS.DASHBOARD} replace />;
  }

  return (
    <div className="app">
      <Sidebar />
      <div className="main">
        <Topbar />
        <div className="content">
          <Outlet />
        </div>
      </div>
      <MobileNav />
    </div>
  );
}
